/**
 * The government-identity gate: the screens between Microsoft sign-in and the portal.
 *
 * Microsoft sign-in establishes *an* account; it does not establish that the person is an
 * official reachable at an agency mailbox. That is what these steps ask for — the official
 * agency address, then a one-time code sent to it — ported from the le-portal-ui-v3
 * prototype's `agency-email` and `verification` steps (src/LoginFlow.tsx).
 *
 * The address and the current step (including the issued challenge) live here rather than
 * in either screen: "Back" from the code screen returns to a filled-in form, the code
 * screen can show the address the code went to, and a challenge that outlives one screen
 * is not re-requested (which would send a second code).
 *
 * Nothing about the code is decided in the browser. Asking for one is a call to the Web
 * API, which answers with how long the code is and when it expires and may be resent; the
 * screens render those. The prototype's third step, agency registration for an
 * unrecognized agency or agent, follows a verified code (RegistrationPage), or opens
 * directly for a previously verified caller whose profile is incomplete. The agency
 * section is saved through one Web API call whether
 * the agency is new or incomplete, then the agent section to the caller's own profile.
 *
 * State is intentionally local and not in Redux. It is not persisted: a reload re-runs the
 * gate.
 */
import { useEffect, useState } from 'react';
import { GovIdPage } from './GovIdPage';
import { OtpPage } from './OtpPage';
import { RegistrationPage } from './RegistrationPage';
import type { ReceivedChallenge, VerificationResult } from '../../api/contracts';
import { FALLBACK_MESSAGE } from '../../api/errors';
import {
  isApiFailure,
  requestVerificationCode,
  saveAgency,
  saveAgent,
} from '../../api/verificationApi';
import { normalizeEmail } from '../../govIdVerification';
import type { AgencyDetails, AgentDetails, RegistrationNeeds } from '../../registration';
import { useAuth } from '../../hooks/useAuth';
import { LoginShell } from '../LoginShell';

export interface GovIdVerificationProps {
  /** Called once verification and any required registration are complete. */
  onVerified: () => void;
  /** Starts at registration for a caller who has already verified their agency address. */
  registration?: { email: string; needs: RegistrationNeeds };
}

/**
 * Where the gate is. One value rather than a challenge plus a flag, so the screens cannot
 * disagree: there is no code screen without a challenge, and reaching registration drops
 * the challenge, which verifying it has already used up.
 */
type Step =
  | { kind: 'email' }
  | { kind: 'code'; challenge: ReceivedChallenge }
  | { kind: 'registration'; needs: RegistrationNeeds };

export function GovIdVerification({ onVerified, registration }: GovIdVerificationProps) {
  const { account, logout } = useAuth();
  const [email, setEmail] = useState(registration?.email ?? '');
  const [confirmEmail, setConfirmEmail] = useState('');
  const [step, setStep] = useState<Step>(
    registration ? { kind: 'registration', needs: registration.needs } : { kind: 'email' },
  );
  const [isSending, setIsSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const handleVerified = ({ registration }: VerificationResult) => {
    if (registration.agency || registration.agent) {
      setStep({ kind: 'registration', needs: registration });
    } else {
      onVerified();
    }
  };

  // Each step replaces the whole screen, which would otherwise leave focus on <body> and
  // announce nothing. Every step's heading is `#login-content-title` with tabIndex -1.
  useEffect(() => {
    document.getElementById('login-content-title')?.focus();
  }, [step.kind]);

  const sendCode = async () => {
    setIsSending(true);
    setSendError(null);

    try {
      setStep({ kind: 'code', challenge: await requestVerificationCode(normalizeEmail(email)) });
    } catch (error) {
      setSendError(isApiFailure(error) ? error.message : FALLBACK_MESSAGE);
    } finally {
      setIsSending(false);
    }
  };

  // A failed save keeps the person on the form with what they typed, to try again. Both saves
  // are safe to repeat, so a retry after the agent save fails resends the agency too.
  const completeRegistration = async ({
    agency,
    agent,
  }: {
    agency?: AgencyDetails;
    agent?: AgentDetails;
  }) => {
    setIsSaving(true);
    setSaveError(null);

    try {
      if (agency !== undefined) {
        await saveAgency(agency);
      }

      if (agent !== undefined) {
        await saveAgent(agent);
      }

      onVerified();
    } catch (error) {
      setSaveError(isApiFailure(error) ? error.message : FALLBACK_MESSAGE);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <LoginShell fullBleed={step.kind === 'registration'}>
      {step.kind === 'registration' ? (
        <RegistrationPage
          email={email}
          microsoftAccount={account?.username ?? ''}
          needs={step.needs}
          onComplete={(details) => {
            void completeRegistration(details);
          }}
          isSaving={isSaving}
          saveError={saveError}
          backDestination={registration ? 'home' : 'email'}
          // Direct entry has no OTP step to return to; sign out rather than bypass registration.
          onBack={() => {
            if (registration) {
              void logout();
            } else {
              setStep({ kind: 'email' });
            }
          }}
        />
      ) : step.kind === 'code' ? (
        <OtpPage
          email={email}
          challenge={step.challenge}
          onChallengeChange={(challenge) => setStep({ kind: 'code', challenge })}
          onVerified={handleVerified}
          // A challenge the server has invalidated cannot be typed against, so dropping
          // it returns to the address step rather than leaving the user retrying a code
          // against something that is gone.
          onBack={() => setStep({ kind: 'email' })}
        />
      ) : (
        <GovIdPage
          email={email}
          confirmEmail={confirmEmail}
          isSending={isSending}
          errorMessage={sendError}
          onEmailChange={(value) => {
            setEmail(value);
            setSendError(null);
          }}
          onConfirmEmailChange={setConfirmEmail}
          onSendCode={() => {
            void sendCode();
          }}
        />
      )}
    </LoginShell>
  );
}
